import { DeleteRounded } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { Box, IconButton, Typography } from '@mui/material';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { renderState, renderStatus, renderFunction } from 'app/modules/tire-custom/component/tire-status';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { TableComponent } from 'app/shared/components/table-component/TableComponent';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { IListData } from 'app/shared/interfaces/listDataInterface';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { ITire } from 'app/shared/model/tire.model';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC } from 'app/shared/util/pagination.constants';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getSortState, translate, Translate } from 'react-jhipster';
import { Link, RouteComponentProps } from 'react-router-dom';
import { getTiresByCodeAndBrandOrModel, reset } from './tire.reducer';
import { showElement } from 'app/shared/util/data-utils';
import { TireState } from 'app/shared/model/enumerations/tire-state.model';
import { TireStatus } from 'app/shared/model/enumerations/tire-status.model';
import { TireFunction } from 'app/shared/model/enumerations/tire-function.model';

export const Tire = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();
  const didMount = useRef(false);

  const formatInteger = (value: unknown) => {
    if (value === null || value === undefined || value === '') return '';
    const n = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(n)) return '';
    return String(Math.round(n));
  };

  const formatDecimal = (value: unknown) => {
    if (value === null || value === undefined || value === '') return '';

    return String(value).replace('.', ',');
  };

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'marking'), props.location.search)
  );

  const tireList = useAppSelector(state => state.tire.entities);
  const loading = useAppSelector(state => state.tire.loading);
  const totalItems = useAppSelector(state => state.tire.totalItems);
  const updateSuccess = useAppSelector(state => state.tire.updateSuccess);
  const [brandOrModelSearch, setBrandOrModelSearch] = useState<string>('');
  const [codeSearch, setCodeSearch] = useState<string>('');
  const isRepresentativeAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.REPRESENTATIVE_ADMIN]));
  const isManufacturerAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_ADMIN]));
  const isTransporterAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TRANSPORTER_ADMIN]));
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));

  const headers: IHeaderItem[] = [
    { key: 'marking', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.marking" /> },
    { key: 'status', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.status" /> },
    // { key: 'state', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.state" /> },
    { key: 'function', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.function" /> },
    { key: 'truckImplement.licensePlate', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.truckImplement" /> },
    { key: 'cpk', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.cpk" /> },    
    { key: 'odometer', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.odometer" /> },
    { key: 'groove', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.groove" /> },
    { key: 'recap', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.recap" /> },
    // { key: 'performance', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.performance" /> },
    // { key: 'lifeExpectancy', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.lifeExpectancy" /> },
    // { key: 'lifePercent', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.lifePercent" /> },
    // { key: 'totalCost', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.totalCost" /> },
    { key: 'tireModel.tireManufacturer.name', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.tireManufacturer" /> },
    { key: 'tireModel.name', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.tireModel" /> },
    { key: 'tireTreadModel.name', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.tireTreadModel" /> },
    { key: 'dot', sortable: true, component: <Translate contentKey="fleetSenseApp.tire.dot" /> },    
  ];

  const filteredHeaders = !isAdmin ? headers.filter(header => header.key !== 'company.name') : headers;

  const stateComponent = (tire: ITire) => {
    return renderState(tire.state ?? TireState.NULL);
  };

  const statusComponent = (tire: ITire) => {
    return renderStatus(tire.status ?? TireStatus.NULL);
  };

  const functionComponent = (tire: ITire) => {
    return renderFunction(tire.function ?? TireFunction.NULL);    
  };

const manufacturerComponent = (tire: ITire) => {
  return tire?.tireModel?.tireManufacturer?.name ?? '';
};

  const renderDeleteButton = (item: ITire) => {
    if (item.status === TireStatus.INSTALLED) return;

    return (
      (isTransporterAdmin || isAdmin) && (
        <IconButton
          component={Link}
          to={`${match.url}/${item.id}/delete${props.location.search}`}
          size="small"
          style={showElement(isAdmin || isRepresentativeAdmin || isManufacturerAdmin || isTransporterAdmin)}
        >
          <DeleteRounded color="error" />
        </IconButton>
      )
    );
  };

  const renderEditButton = (item: ITire) => {
    if (item.status === TireStatus.INSTALLED) return;

    return (
      (isTransporterAdmin || isAdmin) && (
        <IconButton
          component={Link}
          to={`${match.url}/${item.id}/edit`}
          size="small"
          style={showElement(isAdmin || isRepresentativeAdmin || isManufacturerAdmin || isTransporterAdmin)}
        >
          <EditIcon color="secondary" />
        </IconButton>
      )
    );
  };

  const data: IListData[] = [
    { type: 'text', value: 'marking' },
    { type: 'child', value: '', renderTableChild: statusComponent },
    // { type: 'child', value: '', renderTableChild: stateComponent },
    { type: 'child', value: '', renderTableChild: functionComponent },
    {
      type: 'child',
      value: '',
      renderTableChild: row =>
        `${row?.truckImplement?.licensePlate || ''}`,
    },    
    {
      type: 'child',
      value: '',
      renderTableChild: row => formatDecimal(row?.cpk),
    },
    {
      type: 'child',
      value: '',
      renderTableChild: row => formatDecimal(row?.odometer),
    }, 
    {
      type: 'child',
      value: '',
      renderTableChild: row => formatDecimal(row?.groove),
    },
    { type: 'text', value: 'recap' },
    /*   
    {
      type: 'child',
      value: '',
      renderTableChild: row => (
        <Typography color="primary.contrastText" fontSize="0.875rem">
          {formatInteger(row?.performance)}
        </Typography>
      ),
    },
    {
      type: 'child',
      value: '',
      renderTableChild: row => (
        <Typography color="primary.contrastText" fontSize="0.875rem">
          {formatInteger(row?.lifeExpectancy)}
        </Typography>
      ),
    },
    {
      type: 'child',
      value: '',
      renderTableChild: row => (
        <Typography color="primary.contrastText" fontSize="0.875rem">
          {formatInteger(row?.lifePercent)}
        </Typography>
      ),
    },
    */
    /*
    {
      type: 'child',
      value: '',
      renderTableChild: row => formatDecimal(row?.totalCost),
    },
    */
    { type: 'child', value: '', renderTableChild: manufacturerComponent },
    {
      type: 'child',
      value: '',
      renderTableChild: row =>
        `${row?.tireModel?.name || ''} ${row?.tireModel?.specification || ''}`,
    },
    {
      type: 'child',
      value: '',
      renderTableChild: row =>
        `${row?.tireTreadModel?.name || ''} ${row?.tireTreadModel?.specification || ''}`,
    },
    { type: 'text', value: 'dot' },
    { type: 'child', value: '', renderTableChild: renderEditButton },
    { type: 'child', value: '', renderTableChild: renderDeleteButton },
  ];

  const getAllEntities = useCallback(() => {
    dispatch(reset());
    dispatch(
      getTiresByCodeAndBrandOrModel({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        brandOrModelSearch,
        codeSearch,
      })
    );
  }, [paginationState]);

  const sortEntities = () => {
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = (event, page) =>
    setPaginationState({
      ...paginationState,
      activePage: page,
    });

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (updateSuccess) {
      getAllEntities();
    }
  }, [updateSuccess]);

  const searchTypes: ISearchTypes[] = [
    {
      value: brandOrModelSearch,
      setValue: setBrandOrModelSearch,
      placeholder: translate('fleetSenseApp.tire.home.brandOrModelSearch'),
      label: translate('fleetSenseApp.tire.home.brandOrModel'),
    },
    {
      value: codeSearch,
      setValue: setCodeSearch,
      placeholder: translate('fleetSenseApp.tire.home.codeSearch'),
      label: translate('fleetSenseApp.tire.home.codeSearch'),
    },
  ];

  const { match } = props;

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', paddingTop: '16px', textAlign: 'center', padding: '24px' }}>
      <PageHeader
        headingId="tire-heading"
        headingDataCy="TireHeading"
        contentKey="fleetSenseApp.tire.home.title"
        buttonTextKey="fleetSenseApp.tire.home.createLabel"
        match={match}
        showButton={true}
      />

      <PageFilters setActivePage={setActivePage} searchTypes={searchTypes} isCompany={false} showFilters={['filters']} />
      {!loading && <TableComponent listData={data} list={tireList} headers={filteredHeaders} match={match} sortFunction={sort} pagination={paginationState} />}
      {!loading && !tireList.length && <NoData title="marca" />}
      {loading && <Loading />}
      <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={tireList} />
    </Box>
  );
};

export default Tire;
